import { Component, Inject, OnInit } from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NotificationService } from "../../../modules_v2/shared/services/notification.service";
import { ProjectsService } from "../../../modules_v2/projects/services/projects.service";
import { ProjectLookupOption, ProjectLookupsFacade } from "../../../modules_v2/projects/services/project-lookups.facade";
import { TicketService } from "../tickets/ticket.service";

@Component({
  selector: 'convfact-invoiceproject',
  templateUrl: './invoice-dialog-project.component.html',
  styleUrls: ['./invoice-dialog-project.component.css'],
  standalone: false
})
export class InvoiceDialogProjectComponent implements OnInit {
  readonly uid: string;
  readonly invoice: any;
  readonly isTicket: boolean;
  readonly documentNumber: string | null;
  readonly projectControl = new UntypedFormControl(null);
  projects: ProjectLookupOption[] = [];
  projectConfig: any = null;
  currentProjectUid: string | null = null;
  loading = false;
  saving = false;

  constructor(
    public dialogRef: MatDialogRef<InvoiceDialogProjectComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private ticketService: TicketService,
    private projectsService: ProjectsService,
    private lookups: ProjectLookupsFacade,
    private notify: NotificationService,
  ) {
    this.uid = this.data.uid;
    this.invoice = this.data.invoice;
    this.isTicket = this.data.type === 'ticket';
    this.documentNumber = this.invoice ? (this.isTicket ? this.invoice.ticketNumber : this.invoice.invoiceNumber) || null : null;
  }

  get selectedProjectUid(): string | null {
    const value = this.projectControl.value;
    return value && typeof value === 'object' && value.id ? String(value.id) : null;
  }

  get hasChanges(): boolean {
    return this.selectedProjectUid !== this.currentProjectUid;
  }

  ngOnInit(): void {
    this.loading = true;
    const initialUid = this.invoice && this.invoice.projectUid ? String(this.invoice.projectUid) : null;
    const current: Promise<string | null> = initialUid || !this.isTicket
      ? Promise.resolve(initialUid)
      : this.ticketService.loadData(this.uid)
        .then(ticket => ticket && ticket.projectUid ? String(ticket.projectUid) : null)
        .catch(() => null);

    Promise.all([this.lookups.projects(), current])
      .then(([projects, projectUid]) => {
        this.projects = projects;
        this.currentProjectUid = projectUid;
        if (projectUid && !projects.some(project => project.id === projectUid)) {
          this.projects = [{ id: projectUid, display: 'Proyecto no disponible' }, ...projects];
        }
        this.projectConfig = {
          label: 'Proyecto',
          placeholder: this.projects.length ? 'Buscar proyecto…' : 'No tienes proyectos creados',
          options: this.projects,
          strict: true,
          maxResults: 200,
        };
        this.projectControl.setValue(this.projects.find(project => project.id === projectUid) || null, { emitEvent: false });
        this.loading = false;
      })
      .catch(() => {
        this.loading = false;
        this.notify.error('No se pudieron cargar los proyectos');
        this.dialogRef.close(false);
      });
  }

  save(): void {
    if (this.saving || !this.hasChanges) return;
    this.assign(this.selectedProjectUid);
  }

  removeProject(): void {
    if (this.saving || !this.currentProjectUid) return;
    this.assign(null);
  }

  closeDialog(): void {
    if (this.saving) return;
    this.dialogRef.close(false);
  }

  private assign(projectUid: string | null): void {
    this.saving = true;
    this.dialogRef.disableClose = true;
    const documentLabel = this.isTicket ? 'la factura simplificada' : 'la factura';

    this.projectsService.assign(this.isTicket ? 'ticket' : 'invoice', this.uid, projectUid)
      .then(() => {
        this.notify.ok(projectUid ? `Proyecto asignado a ${documentLabel}` : `Proyecto quitado de ${documentLabel}`);
        this.dialogRef.disableClose = false;
        this.dialogRef.close(true);
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.notify.error((error && error.error && error.error.message) || 'No se pudo guardar el proyecto');
      });
  }
}
